import Api, { FetchMethodType } from './Api';

export enum ApplyStep {
  PersonalDetails = 'personalDetails',
  EmploymentDetails = 'employmentDetails',
  References = 'references',
  UploadIncomeVerification = 'incomeVerification',
  UploadLoanInformation = 'loanInformation',
  EnrollmentForm = 'enrollmentForm',
  SignServiceAgreement = 'serviceAgreement',
}

export type ApplyProgress = {
  [ApplyStep.PersonalDetails]: boolean;
  [ApplyStep.EmploymentDetails]: boolean;
  [ApplyStep.References]: boolean;
  [ApplyStep.UploadIncomeVerification]: boolean;
  [ApplyStep.UploadLoanInformation]: boolean;
  [ApplyStep.EnrollmentForm]: boolean;
  [ApplyStep.SignServiceAgreement]: boolean;
};

export const defaultApplyProgress: Readonly<ApplyProgress> = Object.freeze({
  [ApplyStep.PersonalDetails]: false,
  [ApplyStep.EmploymentDetails]: false,
  [ApplyStep.References]: false,
  [ApplyStep.UploadIncomeVerification]: false,
  [ApplyStep.UploadLoanInformation]: false,
  [ApplyStep.EnrollmentForm]: false,
  [ApplyStep.SignServiceAgreement]: false,
});

export enum ResultStage {
  NoFederalLoans = 'no_federal_loans',
  Blocked = 'blocked',
  Estimate = 'estimate',
  Plan = 'plan',
}

export enum Confidence {
  VerifiedInputs = 'verified_inputs',
  DependsOnYou = 'depends_on_you',
  NeedsInfo = 'needs_info',
  Conditional = 'conditional',
}

export enum Terminal {
  PaidOff = 'paid_off',
  PslfForgiveness = 'pslf_forgiveness',
  IdrForgiveness = 'idr_forgiveness',
  NotPaidOff = 'not_paid_off',
}

export enum ResultBasis {
  CreditReport = 'credit_report',
  StaleFile = 'stale_file',
  FreshFile = 'fresh_file',
}

export enum BlockedReason {
  Default = 'default',
  InvalidFile = 'invalid_file',
}

export enum PslfStatus {
  Likely = 'likely',
  Unconfirmed = 'unconfirmed',
  NotEligible = 'not_eligible',
}

export enum FindingCode {
  PlanNotPslfQualifying = 'plan_not_pslf_qualifying',
  SaveEnding = 'save_ending',
  ConfirmCurrentPlan = 'confirm_current_plan',
  PossiblePastDue = 'possible_past_due',
  StaleFile = 'stale_file',
  FileWarnings = 'file_warnings',
  FfelOrPerkins = 'ffel_or_perkins',
  ParentPlusNotModeled = 'parent_plus_not_modeled',
  ConsolidationLineageUnknown = 'consolidation_lineage_unknown',
  PslfReady = 'pslf_ready',
  CurrentPaymentUnknown = 'current_payment_unknown',
  NotInRepayment = 'not_in_repayment',
  PaymentsPaused = 'payments_paused',
  Default = 'default',
  IdrProgressApproximate = 'idr_progress_approximate',
  ScheduleApproximate = 'schedule_approximate',
  NotPaidOff = 'not_paid_off',
  RateAssumed = 'rate_assumed',
  LoanProgramUnverified = 'loan_program_unverified',
}

export enum StepCode {
  UploadNewFile = 'upload_new_file',
  GetOutOfDefault = 'get_out_of_default',
  DownloadFreshFile = 'download_fresh_file',
  ConfirmAccountCurrent = 'confirm_account_current',
  ApplyPslfNow = 'apply_pslf_now',
  PickNewPlanBeforeDeadline = 'pick_new_plan_before_deadline',
  ConsolidateFfelPerkins = 'consolidate_ffel_perkins',
  StayOnCurrentPlan = 'stay_on_current_plan',
  SwitchPlan = 'switch_plan',
  CertifyEmployer = 'certify_employer',
  RestartPausedPayments = 'restart_paused_payments',
  CheckParentPlus = 'check_parent_plus',
  RecertifyYearly = 'recertify_yearly',
}

export enum PaidByPartnerHospital {
  Yes = 'yes',
  No = 'no',
  NotSure = 'notsure',
}

export enum EmployerTypeAnswer {
  Nonprofit = 'nonprofit',
  Government = 'government',
  ForProfit = 'forprofit',
  NotSure = 'notsure',
}

export enum EmployerOwnership {
  Nonprofit = 'nonprofit',
  Government = 'government',
  ForProfit = 'forprofit',
  Unknown = 'unknown',
}

export enum HoursPerWeek {
  ThirtyPlus = '30plus',
  Under30 = 'under30',
  Varies = 'varies',
}

export enum FilingStatus {
  Single = 'single',
  HeadOfHousehold = 'hoh',
  MarriedFilingJointly = 'mfj',
  MarriedFilingSeparately = 'mfs',
}

export enum AgiKind {
  Known = 'known',
  Estimate = 'estimate',
}

/** Whether a known AGI already includes the spouse (a joint Form 1040 line 11) or is the borrower's own. */
export enum AgiScope {
  Individual = 'individual',
  Joint = 'joint',
}

export enum CoverageStatus {
  Matched = 'matched',
  Ambiguous = 'ambiguous',
  NotFound = 'not_found',
}

export enum StateProgramCycleStatus {
  Open = 'open',
  LinkOnly = 'linkOnly',
  VerifyAwardAmount = 'verifyAwardAmount',
}

export interface HealthcareStateProgram {
  id: string;
  state: string;
  name: string;
  summary: string;
  applyUrl: string;
  officialUrl: string;
  credentials: string[];
  maxAnnual?: number;
  maxLifetime?: number;
  cycleStatus: StateProgramCycleStatus;
  asOf: string;
  calculateBenefit: boolean;
}

export interface HospitalFacility {
  id: string;
  name: string;
  city: string;
  state: string;
  zip: string;
  type: string;
  ownershipRaw: string;
}

export interface HospitalChoice {
  facilityId: string;
  name: string;
  city: string;
  state: string;
  type: string;
}

export interface CoverageResult {
  status: CoverageStatus;
  facility: HospitalFacility | null;
  ownership: EmployerOwnership;
  pslfQualifyingType: boolean | null;
  /** Up to 3 hospitals for the borrower to choose from when status is ambiguous. */
  choices: HospitalChoice[];
  cmsReleaseDate: string;
  warnings: string[];
}

export type KnownAgi = { kind: AgiKind.Known; agi: number; scope: AgiScope };

export type EstimatedAgi = {
  kind: AgiKind.Estimate;
  salary: number;
  retirementPct: number;
  pretaxPremiumsMonthly: number;
  hsaFsaAnnual: number;
  otherIncomeAnnual: number;
};

export type AgiInput = KnownAgi | EstimatedAgi;

/** Borrower answers sent in the PUT body. Every field is optional: each screen sends only its own answers. */
export interface StudentLoanCalculationParameters {
  paidByPartnerHospital?: PaidByPartnerHospital | null;
  employerType?: EmployerTypeAnswer | null;
  hoursPerWeek?: HoursPerWeek;
  nonprofitStartYear?: number | null;
  filingStatus?: FilingStatus;
  dependents?: number;
  spouseAgi?: number;
  spouseFederalDebt?: number;
  agi?: AgiInput;
  selectedHospitalFacilityId?: string | null;
}

export type FindingParams = Record<string, string | number | null>;

export interface Finding {
  code: FindingCode;
  params?: FindingParams;
}

export interface Step {
  code: StepCode;
  params?: FindingParams;
}

export interface ValidationIssue {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  loanIndex?: number;
}

export interface GroupOutcome {
  groupId: string;
  description: string;
  balance: number;
  planLabel: string;
  terminal: Terminal;
  monthsToDone: number;
  forgiven: number;
}

export interface PayoffPath {
  id: string;
  label: string;
  isCurrent: boolean;
  pslf: boolean;
  consolidates: boolean;
  firstMonthlyPayment: number;
  monthsToDone: number;
  totalPaid: number;
  /** Tax-free. */
  forgivenPslf: number;
  /** Taxable. */
  forgivenIdr: number;
  /** Placeholder, not a tax estimate. */
  taxPlaceholder: number;
  taxPlaceholderRange: [number, number];
  totalCost: number;
  presentValueCost: number;
  confidence: Confidence;
  groups: GroupOutcome[];
  payments: number[];
}

export interface SavingsRange {
  low: number;
  high: number;
}

export interface PslfOutcome {
  status: PslfStatus;
  tenureMonths: number;
}

export interface StudentLoanPlanResult {
  stage: ResultStage.Estimate | ResultStage.Plan;
  basis: ResultBasis;
  balance: number;
  agi: number;
  householdSize: number;
  pslf: PslfOutcome;
  current: PayoffPath;
  recommended: PayoffPath;
  /** Cheaper path that depends on unconfirmed facts; it sets the top of the savings range. */
  upside: PayoffPath | null;
  allPaths: PayoffPath[];
  /** low === high at the Plan stage; null when the current path never pays off. */
  savings: SavingsRange | null;
  confidence: Confidence;
  fileWillConfirm: string[];
  dependsOn: string[];
  findings: Finding[];
  steps: Step[];
  statePrograms: HealthcareStateProgram[];
}

export interface StudentLoanBlockedResult {
  stage: ResultStage.Blocked;
  reason: BlockedReason;
  findings: Finding[];
  steps: Step[];
  issues: ValidationIssue[];
}

export type StudentLoanResult =
  | { stage: ResultStage.NoFederalLoans }
  | StudentLoanBlockedResult
  | StudentLoanPlanResult;

export interface StudentLoanAssistanceData {
  hospital: CoverageResult;
  result: StudentLoanResult;
  answers: StudentLoanCalculationParameters;
}

export interface PersonalReference {
  firstName?: string;
  lastName?: string;
  relationship?: Relationship;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
}

export enum Relationship {
  Mother = 'mother',
  Father = 'father',
  Child = 'child',
  Friend = 'friend',
  Sister = 'sister',
  Brother = 'brother',
  OtherFamilyMember = 'other family member',
}

export enum IncomeTaxFilingStatus {
  Single = 'Single',
  MarriedFilingJointly = 'Married filing jointly',
  MarriedFilingSeparately = 'Married filing separately',
  HeadOfHousehold = 'Head of household',
}

export enum MaritalStatus {
  Single = 'Single',
  Married = 'Married',
}

export interface ApplicationData {
  employerName?: string;
  hrContactName?: string;
  hrContactEmail?: string;
  ein?: string;
  employerAddress?: string;
  employerCity?: string;
  employerState?: string;
  employerZip?: string;
  employerPhone?: string;
  familySize?: number;
  adjustedGrossIncome?: number;
  incomeTaxFilingStatus?: IncomeTaxFilingStatus;
  maritalStatus?: MaritalStatus;
  driversLicenseState?: string;
  driversLicenseNumber?: string;
  reference1?: PersonalReference;
  reference2?: PersonalReference;
  applyProgress?: ApplyProgress | null;
}

export interface UploadResponse {
  applyProgress?: ApplyProgress | null;
}

export interface StudentAidDataUploadResponse extends UploadResponse {
  result: StudentLoanResult;
  hospital: CoverageResult;
  answers?: StudentLoanCalculationParameters;
}

export interface StudentLoanApi {
  getStudentLoanAssistanceData(applicationId: string): Promise<StudentLoanAssistanceData>;
  updateStudentLoanAssistantData(
    applicationId: string,
    data: StudentLoanCalculationParameters,
  ): Promise<StudentLoanAssistanceData>;
  getApplication(applicationId: string): Promise<ApplicationData>;
  updateApplication(applicationId: string, data: ApplicationData): Promise<ApplicationData>;
  uploadStudentAidData(applicationId: string, file: File): Promise<StudentAidDataUploadResponse>;
  uploadPaystubs(applicationId: string, files: File[]): Promise<UploadResponse>;
  uploadEnrollmentForm(applicationId: string, file: File): Promise<UploadResponse>;
  previewEnrollmentForm(applicationId: string): Promise<string>;
  signEnrollmentForm(applicationId: string, signatureDataUrlPng: string): Promise<string>;
}

export default class StudentLoanRestApi extends Api implements StudentLoanApi {
  async getStudentLoanAssistanceData(applicationId: string): Promise<StudentLoanAssistanceData> {
    return this.fetch<StudentLoanAssistanceData>(`/studentLoan/assistanceData/${applicationId}`);
  }

  async updateStudentLoanAssistantData(
    applicationId: string,
    data: StudentLoanCalculationParameters,
  ): Promise<StudentLoanAssistanceData> {
    return this.fetch<StudentLoanAssistanceData>(`/studentLoan/assistanceData/${applicationId}`, {
      method: FetchMethodType.PUT,
      body: data,
    });
  }

  async getApplication(applicationId: string): Promise<ApplicationData> {
    return this.fetch(`/studentLoan/application/${applicationId}`, {
      method: FetchMethodType.GET,
    });
  }

  async updateApplication(applicationId: string, data: ApplicationData): Promise<ApplicationData> {
    return this.fetch<ApplicationData>(`/studentLoan/application/${applicationId}`, {
      method: FetchMethodType.PUT,
      body: data,
    });
  }

  async uploadStudentAidData(applicationId: string, file: File): Promise<StudentAidDataUploadResponse> {
    const formData = new FormData();

    formData.append('files', file);
    formData.append('applicationId', applicationId);

    const result = await this.fetch<StudentAidDataUploadResponse>(
      `/studentLoan/application/${applicationId}/studentAidData`,
      {
        method: FetchMethodType.POST,
        body: formData,
        resetDefaultHeaders: true,
      },
    );

    return result;
  }

  async uploadPaystubs(applicationId: string, files: File[]): Promise<UploadResponse> {
    const formData = new FormData();

    files.map((file) => formData.append('files', file));

    formData.append('applicationId', applicationId);

    const result = await this.fetch<UploadResponse>(`/studentLoan/application/${applicationId}/paystubs`, {
      method: FetchMethodType.POST,
      body: formData,
      resetDefaultHeaders: true,
    });

    return result;
  }

  async uploadEnrollmentForm(applicationId: string, file: File): Promise<UploadResponse> {
    const formData = new FormData();

    formData.append('files', file);
    formData.append('applicationId', applicationId);

    const result = await this.fetch<UploadResponse>(`/studentLoan/application/${applicationId}/enrollmentForm`, {
      method: FetchMethodType.POST,
      body: formData,
      resetDefaultHeaders: true,
    });

    return result;
  }

  async previewEnrollmentForm(applicationId: string): Promise<string> {
    const result = await this.fetch<string>(`/studentLoan/application/${applicationId}/previewEnrollmentForm`, {
      method: FetchMethodType.POST,
      body: {},
    });
    return result;
  }

  async signEnrollmentForm(applicationId: string, signatureDataUrlPng: string): Promise<string> {
    const result = await this.fetch<string>(`/studentLoan/application/${applicationId}/signEnrollmentForm`, {
      method: FetchMethodType.POST,
      body: { signatureDataUrlPng },
    });
    return result;
  }
}
